import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';

import { ActionItemView, Frequency, TaskStatus, TaskType, TaskView } from '@ndvr/customer-api';

import { FormDateInput } from '@/components/DateInput';
import { MinimalTiptapEditor } from '@/components/minimal-tiptap';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ComboboxOption, FormCombobox } from '@/components/ui/combobox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { FormInput } from '@/components/ui/input';
import { FormMultiCombobox } from '@/components/ui/multi-combobox';
import { FormSelect, SelectOption } from '@/components/ui/select';
import { convertPlainTextToHtml, formatDate, getHouseholdName, hasVisibleTextContent, titleCase } from 'utils';
import { useAuthContext } from 'views/auth/Auth';
import { useCommandCenterHouseholds } from 'views/household/household-queries';

import { useTaskAssigneeMembers } from '../task-assignee-queries';
import { getTaskAssigneeName } from '../task-utils';

const TASK_TYPE_OPTIONS: SelectOption[] = Object.values(TaskType)
    .map((type) => ({
        value: type,
        label: titleCase(type),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

const TASK_STATUS_OPTIONS: SelectOption[] = Object.values(TaskStatus).map((status) => ({
    value: status,
    label: titleCase(status),
}));

const FREQUENCY_OPTIONS: SelectOption[] = Object.values(Frequency).map((f) => ({
    value: f,
    label: titleCase(f),
}));

// Everything the form can hand back; useSubmitTaskForm dispatches on the shape.
export type TaskFormOutput = FormOutputType | FanOutFormOutputType | TenantFormOutputType;

interface TaskFormProps {
    onSubmit: (data: TaskFormOutput) => void;
    onCancel: () => void;
    defaultTaskView?: TaskView;
    defaultFormValues?: Partial<TaskFormType>;
    householdGUID?: string;
    eventGUID?: string;
    actionItemGUID?: string;
    noteGUID?: string;
    initialNoHousehold?: boolean;
    tenantGUID?: string;
    // How the household section behaves (defaults to 'create'):
    //  - 'create': multi-select fan-out + a "not related to a household" tenant toggle.
    //  - 'attach': a single optional household select; picking one attaches a tenant-only task.
    //  - 'locked': no household control; the task keeps its fixed household.
    householdMode?: 'create' | 'attach' | 'locked';
}

export interface TaskFormRef {
    formMethods: UseFormReturn<TaskFormType>;
}

export type TaskFormType = {
    title: string;
    description: string;
    householdOption?: ComboboxOption;
    // For create flow fan-out when no default household
    householdGuids?: string[];
    tenantGuidOption?: SelectOption;
    assigneeOption: ComboboxOption;
    taskTypeOption: SelectOption;
    taskStatusOption: SelectOption;
    dueDate: string;
    isRecurring: boolean;
    frequencyOption?: SelectOption;
};

type FormOutputType = {
    title: string;
    description: string;
    householdGuid: string;
    assignee: string;
    type: TaskType;
    status: TaskStatus;
    dueDate: string;
    eventGUID?: string;
    frequency?: Frequency;
    actionItemGUID?: string;
    noteGUID?: string;
};

// When multi-select is used, parent should create one task per household GUID
type FanOutFormOutputType = Omit<FormOutputType, 'householdGuid'> & {
    householdGuids: string[];
};

// When the user explicitly opts out of a household
export type TenantFormOutputType = Omit<FormOutputType, 'householdGuid'> & {
    noHousehold: true;
    tenantGuid: string;
};

const toFormOutput = (
    data: TaskFormType,
    eventGUID?: string,
    actionItemGUID?: string,
    noteGUID?: string,
): FormOutputType => {
    const householdGuid = data.householdOption?.value;

    if (!householdGuid) {
        throw new Error('A household must be selected to create a task');
    }

    return {
        title: data.title,
        description: data.description,
        householdGuid,
        assignee: data.assigneeOption.value,
        type: data.taskTypeOption.value as TaskType,
        status: data.taskStatusOption.value as TaskStatus,
        dueDate: formatDate(new Date(data.dueDate), 'SERVER'),
        ...(eventGUID && { eventGUID }),
        ...(data.isRecurring && data.frequencyOption?.value
            ? { frequency: data.frequencyOption.value as Frequency }
            : {}),
        ...(actionItemGUID && { actionItemGUID }),
        ...(noteGUID && { noteGUID }),
    };
};

const toTenantOutput = (
    data: TaskFormType,
    eventGUID?: string,
    actionItemGUID?: string,
    noteGUID?: string,
): TenantFormOutputType => {
    const tenantGuid = data.tenantGuidOption?.value;
    if (!tenantGuid) {
        throw new Error('A client base must be selected');
    }
    return {
        noHousehold: true,
        tenantGuid,
        title: data.title,
        description: data.description,
        assignee: data.assigneeOption.value,
        type: data.taskTypeOption.value as TaskType,
        status: data.taskStatusOption.value as TaskStatus,
        dueDate: formatDate(new Date(data.dueDate), 'SERVER'),
        ...(eventGUID && { eventGUID }),
        ...(data.isRecurring && data.frequencyOption?.value
            ? { frequency: data.frequencyOption.value as Frequency }
            : {}),
        ...(actionItemGUID && { actionItemGUID }),
        ...(noteGUID && { noteGUID }),
    };
};

const toFanOutOutput = (
    data: TaskFormType,
    eventGUID?: string,
    actionItemGUID?: string,
    noteGUID?: string,
): FanOutFormOutputType => {
    const householdGuids = data.householdGuids ?? [];
    if (!householdGuids.length) {
        throw new Error('At least one household must be selected to create tasks');
    }
    return {
        title: data.title,
        description: data.description,
        householdGuids,
        assignee: data.assigneeOption.value,
        type: data.taskTypeOption.value as TaskType,
        status: data.taskStatusOption.value as TaskStatus,
        dueDate: formatDate(new Date(data.dueDate), 'SERVER'),
        ...(eventGUID && { eventGUID }),
        ...(data.isRecurring && data.frequencyOption?.value
            ? { frequency: data.frequencyOption.value as Frequency }
            : {}),
        ...(actionItemGUID && { actionItemGUID }),
        ...(noteGUID && { noteGUID }),
    };
};

export const toTaskFormValues = (taskView: TaskView): Partial<TaskFormType> => ({
    title: taskView.title || '',
    description: taskView.description || '',
    householdOption: taskView.householdGUID ? { value: taskView.householdGUID, label: '' } : undefined,
    tenantGuidOption: taskView.tenantGUID ? { value: taskView.tenantGUID, label: '' } : undefined,
    assigneeOption: {
        value: taskView.assignee || '',
        label: '',
    },
    taskTypeOption: {
        value: taskView.type,
        label: titleCase(taskView.type),
    },
    taskStatusOption: {
        value: taskView.status,
        label: titleCase(taskView.status),
    },
    dueDate: taskView.dueDate ? formatDate(taskView.dueDate, 'MM/dd/yyyy') : '',
    isRecurring: !!taskView.frequency,
    frequencyOption: taskView.frequency
        ? { value: taskView.frequency, label: titleCase(taskView.frequency) }
        : undefined,
});

export const toActionItemFormValues = (actionItem: ActionItemView): Partial<TaskFormType> => ({
    title: actionItem.title ?? '',
    description: actionItem.description ?? '',
    assigneeOption: { value: actionItem.assignee ?? '', label: '' },
    dueDate: actionItem.dueDate ? formatDate(actionItem.dueDate, 'MM/dd/yyyy') : '',
});

const InputNames = {
    title: 'title',
    description: 'description',
    householdOption: 'householdOption',
    householdGuids: 'householdGuids',
    tenantGuidOption: 'tenantGuidOption',
    assigneeOption: 'assigneeOption',
    taskTypeOption: 'taskTypeOption',
    taskStatusOption: 'taskStatusOption',
    taskPriorityOption: 'taskPriorityOption',
    dueDate: 'dueDate',
    isRecurring: 'isRecurring',
    frequencyOption: 'frequencyOption',
} as const;

const DEFAULT_VALUES: Partial<TaskFormType> = {
    taskTypeOption: {
        value: TaskType.GENERAL,
        label: titleCase(TaskType.GENERAL),
    },
    taskStatusOption: {
        value: TaskStatus.NEW,
        label: titleCase(TaskStatus.NEW),
    },
    isRecurring: false,
};

export const TaskForm = forwardRef<TaskFormRef, TaskFormProps>((props, ref) => {
    const householdMode = props.householdMode ?? 'create';
    const [noHousehold, setNoHousehold] = useState(
        householdMode === 'create'
            ? (props.initialNoHousehold ?? (!!props.defaultTaskView && !props.defaultTaskView.householdGUID))
            : false,
    );
    const { availableTenants, currentTenant } = useAuthContext();
    const tenantOptions: SelectOption[] = availableTenants.map((t) => ({ value: t.tenantGUID, label: t.name }));
    const { data: households = [] } = useCommandCenterHouseholds();
    const getHouseholdOption = (householdGUID?: string): ComboboxOption | undefined => {
        if (householdGUID && households.length > 0) {
            const household = households.find((h) => h.globalAccountGUID === householdGUID);
            if (household) {
                return {
                    value: household.globalAccountGUID,
                    label: getHouseholdName(household) || 'Unnamed Household',
                };
            }
        }
        return undefined;
    };
    const defaultHouseholdOption = props.householdGUID
        ? getHouseholdOption(props.householdGUID)
        : getHouseholdOption(props.defaultTaskView?.householdGUID);
    const defaultTenantOption: SelectOption | undefined = currentTenant.tenantGUID
        ? { value: currentTenant.tenantGUID, label: currentTenant.name }
        : tenantOptions[0];

    const formMethods = useForm<TaskFormType>({
        defaultValues: props.defaultFormValues
            ? { ...DEFAULT_VALUES, ...props.defaultFormValues }
            : props.defaultTaskView
              ? { ...DEFAULT_VALUES, ...toTaskFormValues(props.defaultTaskView) }
              : {
                    ...DEFAULT_VALUES,
                    ...(defaultHouseholdOption && { householdOption: defaultHouseholdOption }),
                    householdGuids: [],
                    tenantGuidOption: defaultTenantOption,
                },
    });

    const selectedTenantOption = formMethods.watch(InputNames.tenantGuidOption);
    const selectedHouseholdGuids = formMethods.watch(InputNames.householdGuids);
    const selectedHouseholdOption = formMethods.watch(InputNames.householdOption);

    // Edit/single-select: the assignee list must follow the household the user picks (moves can
    // cross tenants), so derive the tenant from the currently selected single household.
    const singleSelectedHouseholdTenantGUID = useMemo(
        () => households.find((h) => h.globalAccountGUID === selectedHouseholdOption?.value)?.tenantGUID,
        [households, selectedHouseholdOption],
    );

    const lockedHouseholdGUID = props.householdGUID ?? props.defaultTaskView?.householdGUID;
    const lockedHouseholdTenantGUID =
        props.tenantGUID ??
        props.defaultTaskView?.tenantGUID ??
        households.find((household) => household.globalAccountGUID === lockedHouseholdGUID)?.tenantGUID;

    const selectedHouseholdTenantGUIDs = useMemo(() => {
        const householdGuidSet = new Set(selectedHouseholdGuids ?? []);
        return [
            ...new Set(
                households
                    .filter((household) => householdGuidSet.has(household.globalAccountGUID))
                    .map((household) => household.tenantGUID)
                    .filter(Boolean),
            ),
        ];
    }, [households, selectedHouseholdGuids]);

    const effectiveTenantGUID = noHousehold
        ? (selectedTenantOption?.value ?? defaultTenantOption?.value)
        : (singleSelectedHouseholdTenantGUID ??
          lockedHouseholdTenantGUID ??
          (selectedHouseholdTenantGUIDs.length === 1 ? selectedHouseholdTenantGUIDs[0] : currentTenant.tenantGUID));

    const { data: assigneeMembers = [] } = useTaskAssigneeMembers(effectiveTenantGUID);
    const assigneeOptions = useMemo(
        () =>
            assigneeMembers.map((member) => ({
                value: member.internalUserGUID,
                label: getTaskAssigneeName(member),
            })),
        [assigneeMembers],
    );

    const clearAssigneeOption = () => {
        formMethods.setValue(InputNames.assigneeOption, { value: '', label: '' }, { shouldDirty: true });
    };

    useImperativeHandle(ref, () => ({
        formMethods,
    }));

    return (
        <Form {...formMethods}>
            <form
                className="flex flex-col gap-3"
                onSubmit={formMethods.handleSubmit((data) => {
                    const eventGUID = props.eventGUID || props.defaultTaskView?.eventGUID;
                    const actionItemGUID = props.actionItemGUID;
                    const noteGUID = props.noteGUID || props.defaultTaskView?.noteGUID;
                    if (householdMode === 'attach') {
                        // Attach to the chosen household, or stay tenant-only when left empty.
                        props.onSubmit(
                            data.householdOption?.value
                                ? toFormOutput(data, eventGUID, actionItemGUID, noteGUID)
                                : toTenantOutput(data, eventGUID, actionItemGUID, noteGUID),
                        );
                    } else if (householdMode === 'locked') {
                        // Fixed household (from defaultTaskView / householdGUID); no scope change.
                        props.onSubmit(toFormOutput(data, eventGUID, actionItemGUID, noteGUID));
                    } else if (noHousehold) {
                        props.onSubmit(toTenantOutput(data, eventGUID, actionItemGUID, noteGUID));
                        // Use multi-select fan-out only when there is no locked household and at least one selection
                    } else if (!defaultHouseholdOption && (data.householdGuids?.length ?? 0) > 0) {
                        props.onSubmit(toFanOutOutput(data, eventGUID, actionItemGUID, noteGUID));
                    } else {
                        props.onSubmit(toFormOutput(data, eventGUID, actionItemGUID, noteGUID));
                    }
                })}
            >
                <FormInput
                    formMethods={formMethods}
                    name={InputNames.title}
                    label="Task Title*"
                    placeholder="Enter task title"
                    required
                />
                <FormField
                    control={formMethods.control}
                    name={InputNames.description}
                    rules={{
                        validate: (value) => hasVisibleTextContent(value || '') || 'Task description is required',
                    }}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Task Description*</FormLabel>
                            <FormControl className="mt-3">
                                <MinimalTiptapEditor
                                    value={convertPlainTextToHtml(field.value || '')}
                                    onChange={field.onChange}
                                    className="w-full"
                                    editorContentClassName="p-5"
                                    output="html"
                                    placeholder="Enter task description here..."
                                    autofocus={true}
                                    editable={true}
                                    editorClassName="focus:outline-hidden"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                {householdMode === 'attach' ? (
                    // Tenant-only task: optionally attach it to a household. A task's household is
                    // sticky once set, so there is no detach/reassign here.
                    <FormCombobox
                        formMethods={formMethods}
                        name={InputNames.householdOption}
                        options={households.map((household) => ({
                            value: household.globalAccountGUID,
                            label: getHouseholdName(household) || 'Unnamed Household',
                        }))}
                        label="Household"
                        selectOptionLabel="Select a household"
                        noOptionsLabel="No households found"
                        placeholder="Leave empty to keep tenant-only"
                        onChange={clearAssigneeOption}
                    />
                ) : householdMode === 'locked' ? null : (
                    <div className="flex flex-col gap-1">
                        {!noHousehold && (
                            <FormMultiCombobox
                                formMethods={formMethods}
                                name={InputNames.householdGuids}
                                options={households.map((household) => ({
                                    value: household.globalAccountGUID,
                                    label: getHouseholdName(household) || 'Unnamed Household',
                                }))}
                                label="Households*"
                                selectOptionLabel="Select households"
                                noOptionsLabel="No households found"
                                placeholder="Select households"
                                rules={{
                                    validate: (val: unknown) =>
                                        noHousehold ||
                                        (Array.isArray(val) && val.length > 0) ||
                                        'At least one household is required',
                                }}
                                onChange={clearAssigneeOption}
                                required
                            />
                        )}
                        {noHousehold ? (
                            <div className="flex flex-col gap-2">
                                <p className="text-muted-foreground text-xs">
                                    Not related to a household.{' '}
                                    <button
                                        type="button"
                                        className="hover:text-foreground underline"
                                        onClick={() => {
                                            setNoHousehold(false);
                                            formMethods.setValue(InputNames.tenantGuidOption, defaultTenantOption);
                                            clearAssigneeOption();
                                        }}
                                    >
                                        Undo
                                    </button>
                                </p>
                                <FormSelect
                                    formMethods={formMethods}
                                    name={InputNames.tenantGuidOption}
                                    options={tenantOptions}
                                    label="Client Base*"
                                    placeholder="Select a client base"
                                    rules={{ required: 'A client base is required' }}
                                    onChange={clearAssigneeOption}
                                />
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="text-muted-foreground hover:text-foreground w-fit text-xs hover:underline"
                                onClick={() => {
                                    setNoHousehold(true);
                                    formMethods.setValue(InputNames.householdGuids, []);
                                    formMethods.setValue(InputNames.householdOption, undefined);
                                    clearAssigneeOption();
                                }}
                            >
                                Not related to a household
                            </button>
                        )}
                    </div>
                )}
                <FormCombobox
                    formMethods={formMethods}
                    name={InputNames.assigneeOption}
                    options={assigneeOptions}
                    label="Assignee*"
                    selectOptionLabel="Select an assignee"
                    noOptionsLabel="No assignees found"
                    placeholder="Select an assignee"
                    required
                />

                <FormDateInput formMethods={formMethods} name={InputNames.dueDate} label="Due Date*" required />
                <FormSelect
                    formMethods={formMethods}
                    name={InputNames.taskTypeOption}
                    options={TASK_TYPE_OPTIONS}
                    label="Task Type"
                    placeholder="Select a task type"
                />
                <FormSelect
                    formMethods={formMethods}
                    name={InputNames.taskStatusOption}
                    options={TASK_STATUS_OPTIONS}
                    label="Task Status"
                    placeholder="Select a task status"
                />
                <FormField
                    control={formMethods.control}
                    name={InputNames.isRecurring}
                    render={({ field }) => (
                        <FormItem className="flex flex-row gap-2">
                            <FormControl>
                                <Checkbox
                                    checked={!!field.value}
                                    onCheckedChange={(val) => field.onChange(val === true)}
                                />
                            </FormControl>
                            <FormLabel>Recurring</FormLabel>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                {formMethods.watch(InputNames.isRecurring) && (
                    <FormSelect
                        formMethods={formMethods}
                        name={InputNames.frequencyOption}
                        options={FREQUENCY_OPTIONS}
                        label="Frequency*"
                        placeholder="Select a frequency"
                        rules={{
                            required: 'Frequency is required for recurring tasks',
                        }}
                    />
                )}
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={props.onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit">Save Task</Button>
                </div>
            </form>
        </Form>
    );
});

TaskForm.displayName = 'TaskForm';
